import React, { useState, useContext, useEffect } from 'react';
import MembershipFlowContext from '../../context/membership_flow_context';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import SocialLogin from '../../../components/react/social_login/social_login';
import Spinner from '../../../components/react/spinner/spinner';
import { checkUserEmail, registerUserRequest } from '../../services/user.api.service';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import { MEMBERSHIPS_WITH_LANDING_PAGE } from '../../constants/membership_flow.constants';
import TermsAndConditions from '../../../components/react/terms_and_conditions/terms_and_conditions';
import { createQueryString } from '../../../utilities/utilities';
const REGISTER_PATH = '/register';

import './default_register_form.scss';

const DefaultRegisterForm = ({ setEmailRecipient }) => {
  const { text1, text2, packageShortName, authToken, paymentPage, returnTo, isSignedIn } =
    useContext(MembershipFlowContext);
  const [alertData, setAlertData] = useState(null);
  const [email, setEmail] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginPath, setLoginPath] = useState('/login');
  const isPackageWithLandingPage = MEMBERSHIPS_WITH_LANDING_PAGE.includes(packageShortName);

  useEffect(() => {
    if (window.location.search) {
      if (window.location.search.includes('return_to')) {
        setLoginPath(`/login${window.location.search}`);
      } else {
        setLoginPath(`/login?return_to=${window.location.href}`);
      }
    }
  }, []);
  /**
   * When the form is submitted, it does a request to verify if user is valid or not:
   * - If not valid (email already exists) it displays an error message.
   * - If valid, it does a register user request and it sets setEmailRecipient
   *   in order to display the correct message to the user.
   *
   * @param {Event} event - Event triggered by browser
   * @returns {undefined}
   */
  const requestEmailVerification = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      const checkUserResponse = await checkUserEmail(email);
      if (!checkUserResponse.valid) {
        setEmailExists(true);
        setIsLoading(false);
        return;
      }

      await registerUserRequest({ email, packageShortName, paymentPage, returnTo });
      setIsLoading(false);
      setEmailRecipient(email);
      // This triggers redirection to /verification-sent
      setVerificationEmailSent(true);
    } catch {
      setIsLoading(false);
      setAlertData({type: 'error', message: 'Error on registration, please try again.' });
    }
  };

  const renderText1 = Boolean(text1) && (
    <div data-testid='text-1' className='text-1'>
      {text1}
    </div>
  );

  const renderText2 = Boolean(text2) && (
    <div data-testid='text-2' className='text-2'>
      {text2}
    </div>
  );

  const renderAccountBenefits = (
    <div className={`text-gray-li small-text ${isPackageWithLandingPage ? 'mt-3' : 'mb-45'}`}>
      Register here to get access to the community, news and information, and updates about benefits
      and other offers.
    </div>
  );

  const socialReturnTo = `${REGISTER_PATH}/${packageShortName}/your-details${createQueryString({
    package_short_name: packageShortName,
    payment_page: paymentPage,
    return_to: returnTo,
  })}`;

  const renderSocialLogins = (
    <div data-testid='social-logins' className='mt-3 mb-3'>
      <SocialLogin
        provider={'google_oauth2'}
        authToken={authToken}
        buttonText='Sign up with Google'
        iconClass='icon-google'
        redirectTo={socialReturnTo}
      />
      <SocialLogin
        provider={'linkedin'}
        authToken={authToken}
        buttonText='Sign up with LinkedIn'
        iconClass='icon-linkedin'
        redirectTo={socialReturnTo}
      />
      <SocialLogin
        provider={'facebook'}
        authToken={authToken}
        buttonText='Sign up with Facebook'
        iconClass='icon-facebook-square'
        redirectTo={socialReturnTo}
      />
    </div>
  );

  const renderSeparator = (
    <section className='separator mt-3 mb-3'>
      <span className='separator-title'>or use your email</span>
    </section>
  );

  const renderUserAgreement = (
    <div className={`mt-2 ${!isPackageWithLandingPage && 'mb-45'}`}>
      <TermsAndConditions />
    </div>
  );

  const renderExistingEmailError = (
    <div data-testid='existing-email-error' className='existing-email-error mt-2'>
      This email is already associated with an existing Devex account.
      <a className='btn-link ms-1' href={loginPath}>
        Sign in
      </a>
      .
    </div>
  );

  const renderSignInLink = (
    <a className='btn-link sign-in-link' href={loginPath}>
      Sign in to your account
    </a>
  );

  const renderEmailForm = (
    <form role='form' onSubmit={requestEmailVerification}>
      <div className='form-group mb-3'>
        <label className='form-label d-none d-sm-none d-md-block' htmlFor='register-email'>
          Email
        </label>
        <input
          id='register-email'
          data-testid='register-email'
          type='email'
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          className='form-control'
        ></input>
        {emailExists && renderExistingEmailError}
      </div>
      {renderUserAgreement}
      <button
        data-testid='register-button'
        type='submit'
        className='btn btn-dark w-100 btn-lg mt-3 mb-3'
      >
        <strong>Get started</strong>
      </button>
      {renderSignInLink}
      <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
    </form>
  );

  if (verificationEmailSent) {
    const redirectTo = `${window.location.pathname}/verification-sent`;
    return <Redirect push to={redirectTo} />;
  }

  if (isSignedIn) {
    const redirectTo = `${window.location.pathname}/get-started${window.location.search}`;
    return <Redirect push to={redirectTo} />;
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='default-register-form' className='default-register-form'>
        {renderText1}
        {renderText2}
        {renderAccountBenefits}
        {renderSocialLogins}
        {renderSeparator}
        {renderEmailForm}
      </div>
    </>
  );
};

DefaultRegisterForm.propTypes = {
  setEmailRecipient: PropTypes.func.isRequired,
};

export default DefaultRegisterForm;
